import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  createAccountBudget,
  deleteAccountBudget,
  fetchAccountBudgets,
  fetchBudgetVsActual,
  fetchChartOfAccounts,
  fetchFinancialPeriods,
  updateAccountBudget,
} from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import CustomizerContext from "../../../_helper/Customizer";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import TablePageMetricCard from "../Common/TablePageMetricCard";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad, runRefresh } from "../Common/credoraToast";
import { AccountingFilterApplyButtonInline } from "./AccountingFilterApplyButton";
import AccountingIconActions from "./AccountingIconActions";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import { accountingReportBreadcrumbs } from "./accountingReportShell";
import { useLatestRef } from "./accountingUtils";

const TABS = [
  { id: "comparison", label: "Budget vs actual" },
  { id: "budgets", label: "Budget lines" },
];

const emptyForm = () => ({ account_id: "", budget_amount: "" });

const BudgetVsActualPage = ({ reportMode = false }) => {
  const crumbs = accountingReportBreadcrumbs({ reportMode, title: "Budget vs Actual" });
  const { confirm } = useConfirmDialog();
  const { layoutURL } = useContext(CustomizerContext);
  const pub = process.env.PUBLIC_URL || "";
  const periodsPath = `${pub}/credora/accounting/financial-periods/${layoutURL}`;

  const [tab, setTab] = useState("comparison");
  const [branches, setBranches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [periodId, setPeriodId] = useState("");
  const [budgets, setBudgets] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  useEffect(() => {
    if (!branchId) {
      setPeriodsLoading(false);
      return;
    }
    setPeriodsLoading(true);
    fetchFinancialPeriods(branchId)
      .then((rows) => {
        const list = rows || [];
        setPeriods(list);
        setPeriodId(list.length ? String(list[0].id) : "");
      })
      .catch((err) => setError(formatApiError(err, "Failed to load financial periods.")))
      .finally(() => setPeriodsLoading(false));
  }, [branchId]);

  const load = useCallback(() => {
    if (!branchId || !periodId) {
      setReport(null);
      setBudgets([]);
      setLoading(false);
      return Promise.resolve();
    }
    setLoading(true);
    setError("");
    return runLoad(
      () =>
        Promise.all([
          fetchAccountBudgets(branchId, periodId).then(setBudgets),
          fetchBudgetVsActual(branchId, periodId).then(setReport),
          fetchChartOfAccounts({ branch_id: branchId, active_only: true }).then(setAccounts),
        ]),
      { errorMessage: "Failed to load budget data." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load budget data.")))
      .finally(() => setLoading(false));
  }, [branchId, periodId]);

  useEffect(() => {
    if (!periodsLoading) load();
  }, [load, periodsLoading]);

  const loadRef = useLatestRef(load);

  const budgetedAccountIds = useMemo(
    () => new Set(budgets.map((b) => String(b.account_id))),
    [budgets]
  );

  const availableAccounts = useMemo(() => {
    if (editingBudget) {
      return accounts.filter(
        (a) => String(a.id) === String(editingBudget.account_id) || !budgetedAccountIds.has(String(a.id))
      );
    }
    return accounts.filter((a) => !budgetedAccountIds.has(String(a.id)));
  }, [accounts, budgetedAccountIds, editingBudget]);

  const openCreate = () => {
    setEditingBudget(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (budget) => {
    setEditingBudget(budget);
    setForm({
      account_id: String(budget.account_id),
      budget_amount: String(budget.budget_amount),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingBudget(null);
    setForm(emptyForm());
  };

  const removeBudget = useCallback(
    (budget) => {
      confirm({
        title: `Delete budget for ${budget.account_code || budget.account_name}?`,
        message: "This removes the budget line for this period. Actuals in the comparison report are unchanged.",
        confirmLabel: "Delete",
        tone: "danger",
        successMessage: "Budget line deleted.",
        onConfirm: async () => {
          await deleteAccountBudget(budget.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingBudget) {
        await runCrud(
          () => updateAccountBudget(editingBudget.id, { budget_amount: Number(form.budget_amount) }),
          { successMessage: "Budget line updated." }
        );
      } else {
        await runCrud(
          () =>
            createAccountBudget({
              branch_id: branchId,
              period_id: periodId,
              account_id: form.account_id,
              budget_amount: Number(form.budget_amount),
            }),
          { successMessage: "Budget line added." }
        );
      }
      closeModal();
      load();
    } finally {
      setSaving(false);
    }
  };

  const renderActions = useCallback(
    (budget) => {
      if (!budget) return null;
      return (
        <AccountingIconActions
          onEdit={() => openEdit(budget)}
          onDelete={() => removeBudget(budget)}
          editLabel="Edit budget"
          deleteLabel="Delete budget"
        />
      );
    },
    [removeBudget]
  );

  const reportColumns = useMemo(
    () => {
      const cols = [
        { name: "Code", selector: (r) => r.account_code, width: "90px" },
        { name: "Account", selector: (r) => r.account_name, grow: 2 },
        { name: "Budget", cell: (r) => formatTZS(r.budget_amount), right: true, width: "120px" },
        { name: "Actual", cell: (r) => formatTZS(r.actual_amount), right: true, width: "120px" },
        {
          name: "Variance",
          cell: (r) => (
            <span className={Number(r.variance) < 0 ? "text-danger" : "text-success"}>{formatTZS(r.variance)}</span>
          ),
          right: true,
          width: "120px",
        },
      ];
      if (!reportMode) {
        cols.push({
          name: "",
          cell: (r) => renderActions(budgets.find((b) => String(b.id) === String(r.budget_id))),
          width: "88px",
          ignoreRowClick: true,
        });
      }
      return cols;
    },
    [budgets, renderActions, reportMode]
  );

  const budgetColumns = useMemo(
    () => [
      { name: "Code", selector: (r) => r.account_code, width: "90px" },
      { name: "Account", selector: (r) => r.account_name, grow: 2 },
      { name: "Budget", cell: (r) => formatTZS(r.budget_amount), right: true, width: "130px" },
      { name: "Updated", cell: (r) => formatDate(r.updated_at), width: "110px" },
      {
        name: "",
        cell: (r) => renderActions(r),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [renderActions]
  );

  const visibleTabs = reportMode ? TABS.filter((t) => t.id === "comparison") : TABS;
  const busy = periodsLoading || loading;
  const selectedPeriod = periods.find((p) => String(p.id) === String(periodId));

  return (
    <Fragment>
      <Breadcrumbs mainTitle={crumbs.mainTitle} parent={crumbs.parent} title={crumbs.title} />
      <div className={`container-fluid${reportMode ? " credora-finance-reports pb-5 mb-3" : ""}`}>
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card mb-3">
          <div className="card-body d-flex flex-wrap gap-2 align-items-end">
            <FormGroup className="mb-0">
              <Label>Branch</Label>
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "160px" }}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={periodsLoading}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label>Period</Label>
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "180px" }}
                value={periodId}
                onChange={(e) => setPeriodId(e.target.value)}
                disabled={periodsLoading || !periods.length}
              >
                {!periods.length ? (
                  <option value="">No periods</option>
                ) : (
                  periods.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.status})</option>
                  ))
                )}
              </Input>
            </FormGroup>
            <AccountingFilterApplyButtonInline
              label="Refresh"
              size="sm"
              onClick={() => runRefresh(load, { label: "Budget vs actual" })}
              disabled={!periodId || busy}
            />
            {!reportMode && (
              <AccountingFilterApplyButtonInline
                label="Add budget"
                size="sm"
                onClick={openCreate}
                disabled={!periodId || busy || !availableAccounts.length}
              />
            )}
          </div>
        </div>

        {busy ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : !periodId ? (
          <Alert color="info" className="mb-0">
            No financial period exists for this branch.{" "}
            <Link to={periodsPath}>Create a financial period</Link> first, then add budget lines per account.
          </Alert>
        ) : (
          <>
            {report && (
              <Row className="g-3 mb-3">
                <Col md="4"><TablePageMetricCard title="Total budget" value={formatTZS(report.total_budget)} subtitle={selectedPeriod?.name} /></Col>
                <Col md="4"><TablePageMetricCard title="Total actual" value={formatTZS(report.total_actual)} subtitle="Posted GL activity in period" /></Col>
                <Col md="4"><TablePageMetricCard title="Variance" value={formatTZS(report.total_variance)} subtitle="Budget minus actual" /></Col>
              </Row>
            )}

            <Nav pills className="nav-pills nav-primary mb-3 flex-wrap">
              {visibleTabs.map((t) => (
                <NavItem key={t.id}>
                  <NavLink
                    className={tab === t.id ? "active" : ""}
                    onClick={() => setTab(t.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {t.label}
                    {t.id === "budgets" ? ` (${budgets.length})` : ""}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={tab}>
              <TabPane tabId="comparison">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Comparison report</h5>
                    {!reportMode && (
                      <Button color="primary" size="sm" onClick={openCreate} disabled={!availableAccounts.length}>
                        Add budget line
                      </Button>
                    )}
                  </div>
                  <div className="card-body p-0">
                    {report?.rows?.length ? (
                      <DataTable columns={reportColumns} data={report.rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
                    ) : (
                      <div className="text-center text-muted py-5 px-3">
                        No budget lines for {selectedPeriod?.name || "this period"}. Add a budget line per GL account; actuals are pulled from posted journals.
                      </div>
                    )}
                  </div>
                  {report && (
                    <div className="card-footer d-flex flex-wrap justify-content-end gap-4">
                      <strong>Budget: {formatTZS(report.total_budget)}</strong>
                      <strong>Actual: {formatTZS(report.total_actual)}</strong>
                      <strong>Variance: {formatTZS(report.total_variance)}</strong>
                    </div>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="budgets">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Budget lines — {selectedPeriod?.name}</h5>
                    <Button color="primary" size="sm" onClick={openCreate} disabled={!availableAccounts.length}>
                      Add budget line
                    </Button>
                  </div>
                  <div className="card-body p-0">
                    {budgets.length ? (
                      <DataTable columns={budgetColumns} data={budgets} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
                    ) : (
                      <div className="text-center text-muted py-5 px-3">
                        No budget lines yet. Click <strong>Add budget line</strong> to create one.
                      </div>
                    )}
                  </div>
                </div>
              </TabPane>
            </TabContent>
          </>
        )}

        <Modal isOpen={modalOpen} toggle={closeModal}>
          <Form onSubmit={handleSave}>
            <ModalHeader toggle={closeModal}>{editingBudget ? "Edit budget line" : "Add budget line"}</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Account</Label>
                <Input
                  type="select"
                  required
                  disabled={Boolean(editingBudget)}
                  value={form.account_id}
                  onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                >
                  <option value="">Select account</option>
                  {(editingBudget
                    ? accounts.filter((a) => String(a.id) === String(editingBudget.account_id))
                    : availableAccounts
                  ).map((a) => (
                    <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                  ))}
                </Input>
                {!editingBudget && !availableAccounts.length && (
                  <small className="text-muted">All accounts already have a budget for this period.</small>
                )}
              </FormGroup>
              <FormGroup>
                <Label>Budget amount</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={form.budget_amount}
                  onChange={(e) => setForm({ ...form, budget_amount: e.target.value })}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={closeModal}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving…" : editingBudget ? "Update" : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default BudgetVsActualPage;
